// ExpiryReport.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import '../report.css';
import ApiService from '../../../../services/apiService';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';

// ---------------- HELPERS ----------------
const numberFmt = (n, digits = 0) =>
  new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number.isFinite(+n) ? +n : 0);

const dateToInputValue = (d) => new Date(d).toISOString().slice(0, 10); // yyyy-mm-dd

const prettifyHeader = (key) =>
  String(key)
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const isISODateLike = (v) =>
  typeof v === 'string' &&
  // "2025-12-01T00:00:00" or "2025-12-01"
  /^\d{4}-\d{2}-\d{2}(T.*)?$/.test(v) &&
  !Number.isNaN(Date.parse(v));

const normalizeCell = (key, val) => {
  // Handle null/undefined
  if (val === null || val === undefined) return '—';

  // Handle weird BatchNo coming as {} sometimes
  if (key === 'BatchNo') {
    if (typeof val === 'object') {
      // {} -> no batch
      return Object.keys(val || {}).length ? JSON.stringify(val) : '—';
    }
    return String(val).trim() || '—';
  }

  // Generic objects/arrays
  if (typeof val === 'object') {
    try {
      return JSON.stringify(val);
    } catch {
      return String(val);
    }
  }

  return val;
};

const formatCellForView = (val) => {
  if (val === '—') return val;

  // Date formatting
  if (isISODateLike(val)) {
    const d = new Date(val);
    return new Intl.DateTimeFormat('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }).format(d);
  }

  // Number formatting
  if (Number.isFinite(+val) && String(val).trim() !== '') {
    // Qty should usually be integer; if your API sends decimals it will still show fine
    const asNum = +val;
    const digits = Number.isInteger(asNum) ? 0 : 2;
    return numberFmt(asNum, digits);
  }

  return String(val);
};

const inferAlign = (key, sampleValues) => {
  const nonEmpty = sampleValues.filter((v) => v !== '—' && v !== '' && v !== null && v !== undefined);

  if (key.toLowerCase().includes('date')) return 'center';

  // All numeric => right
  const allNumeric = nonEmpty.length > 0 && nonEmpty.every((v) => typeof v === 'number' || (typeof v === 'string' && v.trim() !== '' && Number.isFinite(+v)));

  return allNumeric ? 'right' : 'left';
};

// ---------------- COMPONENT ----------------
const ExpiryReport = ({ Currency }) => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 50,
    totalRecords: 0,
    totalPages: 0,
    hasPrevious: false,
    hasNext: false,
  });

  // Default last 10 days
  const now = new Date();
  const tenDaysAgo = new Date(now);
  tenDaysAgo.setDate(now.getDate() - 10);

  const [formData, setFormData] = useState({
    fromDate: dateToInputValue(tenDaysAgo),
    toDate: dateToInputValue(now),
    batchNo: '', // ✅ NEW (Batch filter input)
  });

  // ---------------- Fetch Branches ----------------
  const fetchBranches = async () => {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_BRANCH');
      const list = res?.data?.result || [];

      setBranches(
        list.map((b) => ({
          value: b.id,
          label: b.branchName,
        }))
      );
    } catch (e) {
      console.error('Failed to fetch branches', e);
    }
  };

  // ---------------- Fetch Expiry Data ----------------
  const fetchData = async (page = 1, size = pagination.pageSize) => {
    setLoading(true);
    setErr('');

    try {
      const apiService = new ApiService();
      const params = new URLSearchParams();

      // If backend supports pagination, keep these enabled:
      params.append('PageNo', page);
      params.append('PageSize', size);

      params.append('ReportType', 'Expiry Report'); // ⭐ important
      params.append('Measure', 'Revenue'); // keep if backend requires it
      params.append('StartDate', formData.fromDate);
      params.append('EndDate', formData.toDate);

      // ✅ NEW: BatchNo filter (send null if empty)
      const batch = String(formData.batchNo || '').trim();
      params.append('BatchNo', batch ? batch : 'null');

      params.append('Comparison', 'No Comparison');
      params.append('BranchId', selectedBranch?.value || 0); // 0 = all branches

      const res = await apiService.getApiParamater('GET_SALE_SUMMERY', `?${params.toString()}`);

      const result = Array.isArray(res?.data?.result) ? res.data.result : [];

      const normalized = result.map((obj) => {
        const out = {};
        Object.keys(obj || {}).forEach((k) => {
          out[k] = normalizeCell(k, obj[k]);
        });
        return out;
      });

      setRows(normalized);

      // Pagination (backend may return nulls; keep it safe)
      const pd = res?.data?.paginationData || {};
      const totalRecords = Number.isFinite(+pd.totalRecords) ? +pd.totalRecords : normalized.length;
      const pageSize = Number.isFinite(+pd.pageSize) ? +pd.pageSize : size;
      const totalPages = Number.isFinite(+pd.totalPages) ? +pd.totalPages : pageSize > 0 ? Math.max(1, Math.ceil(totalRecords / pageSize)) : 1;

      setPagination({
        currentPage: Number.isFinite(+pd.currentPage) ? +pd.currentPage : page,
        pageSize,
        totalRecords,
        totalPages,
        hasPrevious: !!pd.hasPrevious || page > 1,
        hasNext: !!pd.hasNext || page < totalPages,
      });
    } catch (e) {
      console.error(e);
      setErr('Failed to fetch report. Please try again.');
      setRows([]);
      setPagination((p) => ({ ...p, totalRecords: 0, totalPages: 0 }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  // ---------------- Dynamic Columns ----------------
  const columns = useMemo(() => {
    if (!rows.length) return [];

    // Union of keys (so if some rows have extra fields, we still show them)
    const keySet = new Set();
    rows.forEach((r) => Object.keys(r || {}).forEach((k) => keySet.add(k)));

    const keys = Array.from(keySet);

    return keys.map((key) => {
      const samples = rows.slice(0, 30).map((r) => r?.[key]);
      return {
        key,
        label: prettifyHeader(key),
        align: inferAlign(key, samples),
      };
    });
  }, [rows]);

  // ---------------- Sorting ----------------
  const [sortState, setSortState] = useState({ key: '', dir: 'asc' });

  const sortedRows = useMemo(() => {
    if (!rows.length) return [];

    const { key, dir } = sortState;
    if (!key) return rows;

    const copy = [...rows];

    const cmp = (a, b) => {
      const av = a?.[key];
      const bv = b?.[key];

      // Dates
      if (isISODateLike(av) && isISODateLike(bv)) {
        const ad = new Date(av).getTime();
        const bd = new Date(bv).getTime();
        return dir === 'asc' ? ad - bd : bd - ad;
      }

      // Numbers
      const aNum = Number.isFinite(+av) ? +av : null;
      const bNum = Number.isFinite(+bv) ? +bv : null;
      if (aNum !== null && bNum !== null) return dir === 'asc' ? aNum - bNum : bNum - aNum;

      // Strings fallback
      const as = av === null || av === undefined ? '' : String(av);
      const bs = bv === null || bv === undefined ? '' : String(bv);
      return dir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    };

    copy.sort(cmp);
    return copy;
  }, [rows, sortState]);

  const onSort = (key) => setSortState((prev) => (prev.key !== key ? { key, dir: 'asc' } : { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }));

  // ---------------- Totals (auto for numeric cols) ----------------
  const totalsByKey = useMemo(() => {
    const totals = {};
    if (!sortedRows.length) return totals;

    columns.forEach((c) => {
      // ✅ Optional but recommended: don't total BatchNo
      if (c.key === 'BatchNo') return;

      const sum = sortedRows.reduce((acc, r) => {
        const v = r?.[c.key];
        return Number.isFinite(+v) ? acc + +v : acc;
      }, 0);

      // Only show total if at least one numeric value exists
      const hasAnyNumeric = sortedRows.some((r) => Number.isFinite(+r?.[c.key]));
      if (hasAnyNumeric) totals[c.key] = sum;
    });

    return totals;
  }, [sortedRows, columns]);

  // ---------------- Handlers ----------------
  const onChangeRange = (e) => setFormData((old) => ({ ...old, [e.target.name]: e.target.value }));
  const onSearch = () => fetchData(1, pagination.pageSize);

  const goPrev = () => fetchData(Math.max(1, pagination.currentPage - 1), pagination.pageSize);
  const goNext = () => fetchData(pagination.currentPage + 1, pagination.pageSize);

  // ---------------- CSV Export ----------------
  const exportCSV = () => {
    const cols = columns;
    const headers = cols.map((c) => `"${c.label}"`).join(',');

    const body = sortedRows
      .map((r) =>
        cols
          .map((c) => {
            const raw = r?.[c.key];
            const val = raw === null || raw === undefined ? '' : String(raw);
            return `"${val.replace(/"/g, '""')}"`;
          })
          .join(',')
      )
      .join('\n');

    const csv = `${headers}\n${body}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `expiry-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="p-5">
      <button className="addItem-btn mb-3" onClick={() => navigate(-1)}>
        ← Back
      </button>

      {/* Filters */}
      <div className="row g-3 align-items-end">
        <div className="col-md-3">
          <label className="form-label">Select Branch</label>
          <Select
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 999999 }),
              menu: (base) => ({ ...base, zIndex: 999999 }),
            }}
            value={selectedBranch}
            onChange={(opt) => setSelectedBranch(opt)}
            options={branches}
            placeholder="Select Branch"
            isClearable
          />
        </div>

        {/* ✅ NEW: BatchNo Filter */}
        <div className="col-md-3">
          <label className="form-label">Batch No</label>
          <input
            type="text"
            className="form-control"
            placeholder="Enter Batch No (optional)"
            value={formData.batchNo}
            onChange={(e) => setFormData((old) => ({ ...old, batchNo: e.target.value }))}
          />
        </div>

        <div className="col-md-3">
          <label className="form-label">From Date</label>
          <input type="date" className="form-control" name="fromDate" value={formData.fromDate} onChange={onChangeRange} />
        </div>

        <div className="col-md-3">
          <label className="form-label">To Date</label>
          <input type="date" className="form-control" name="toDate" value={formData.toDate} onChange={onChangeRange} />
        </div>

        <div className="col-md-3 d-flex gap-3 align-items-end">
          <button className="addItem-btn" onClick={onSearch} disabled={loading}>
            {loading ? 'Loading…' : 'Search'}
          </button>

          <button className="btn btn-outline-secondary" onClick={exportCSV} disabled={!sortedRows.length || !columns.length}>
            Export CSV
          </button>
        </div>
      </div>

      {/* Pagination controls */}
      <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
        <div className="text-muted">
          Records: <b>{pagination.totalRecords}</b>
          {pagination.totalPages ? (
            <>
              {' '}
              | Page <b>{pagination.currentPage}</b> / <b>{pagination.totalPages}</b>
            </>
          ) : null}
        </div>

        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary" onClick={goPrev} disabled={loading || pagination.currentPage <= 1}>
            Prev
          </button>
          <button
            className="btn btn-outline-secondary"
            onClick={goNext}
            disabled={loading || (pagination.totalPages ? pagination.currentPage >= pagination.totalPages : !pagination.hasNext)}
          >
            Next
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="POsgridshad rounded p-0 mt-3">
        {err && <div className="alert alert-danger m-3">{err}</div>}

        <div className="custom-table-container">
          <table className="custom-table">
            <thead>
              <tr>
                {(columns.length ? columns : []).map((c) => (
                  <th
                    key={c.key}
                    onClick={() => onSort(c.key)}
                    className={c.align === 'right' ? 'text-end' : c.align === 'center' ? 'text-center' : ''}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                    title="Click to sort"
                  >
                    {c.label}
                    {sortState.key === c.key ? (sortState.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {!loading && sortedRows.length === 0 && (
                <tr>
                  <td colSpan={Math.max(columns.length, 1)} className="text-center p-4">
                    No data found
                  </td>
                </tr>
              )}

              {!loading &&
                sortedRows.map((r, index) => (
                  <tr key={index}>
                    {columns.map((c) => {
                      const raw = r?.[c.key];

                      // ✅ IMPORTANT: Don't format BatchNo with commas
                      const view = c.key === 'BatchNo' ? (raw === '—' ? '—' : String(raw)) : formatCellForView(raw);

                      return (
                        <td
                          key={c.key}
                          className={c.align === 'right' ? 'text-end' : c.align === 'center' ? 'text-center' : ''}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {view}
                        </td>
                      );
                    })}
                  </tr>
                ))}

              {loading && (
                <tr>
                  <td colSpan={Math.max(columns.length, 1)} className="text-center p-4">
                    Loading…
                  </td>
                </tr>
              )}
            </tbody>

            {/* Footer Totals */}
            {sortedRows.length > 0 && columns.length > 0 && (
              <tfoot>
                <tr className="fw-bold bg-light">
                  {columns.map((c, idx) => {
                    const hasTotal = Object.prototype.hasOwnProperty.call(totalsByKey, c.key);
                    const totalVal = totalsByKey[c.key];

                    // Put "Total" in first column, totals in numeric columns, blank otherwise.
                    if (idx === 0) return <td key={c.key}>Total</td>;

                    if (!hasTotal) return <td key={c.key} />;

                    return (
                      <td key={c.key} className={c.align === 'right' ? 'text-end' : c.align === 'center' ? 'text-center' : ''}>
                        {formatCellForView(totalVal)}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => state;
export default connect(mapStateToProps)(ExpiryReport);
